import { useState, useEffect, lazy, Suspense } from "react";
import { useDataset } from "@inrupt/solid-ui-react";
import { Box, Typography, Alert, CircularProgress, ToggleButtonGroup, ToggleButton } from "@mui/material";

import RoadScene from "./three-scenes/RoadScene";
import Header from "./Header";

const SkyScene = lazy(() => import("./three-scenes/SkyScene"));

const SOLID_POD_URI =
  "https://storage.inrupt.com/0ceecbd1-59d2-4858-8e73-a819d5bdb943/commonplace-book/index.ttl";

function Commonplace({
  handleShowLanding,
  handleShowKnowledgeLists,
  handleShowCommonplace,
  sessionActive,
  listsToDisplay,
  currentView = "commonplace",
}) {
  const { dataset, error } = useDataset(SOLID_POD_URI);
  const [quoteCount, setQuoteCount] = useState(null);
  const [sceneView, setSceneView] = useState("road");

  const quoteListForScene =
    sessionActive && Array.isArray(listsToDisplay) && listsToDisplay.length > 0
      ? listsToDisplay
      : [dataset];

  const communalLoading = !dataset && !error;
  const communalError = Boolean(error);

  // Reset quote count when scene data changes so we don't show stale empty state
  useEffect(() => {
    setQuoteCount(null);
  }, [quoteListForScene]);

  return (
    <>
      <Header
        handleShowLanding={handleShowLanding}
        handleShowCommonplace={handleShowCommonplace}
        handleShowKnowledgeLists={handleShowKnowledgeLists}
        currentView={currentView}
      />
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: (t) => t.spacing(2), mb: (t) => t.spacing(1) }}>
        <Typography variant="body2" color="text.secondary">
          {sceneView === "road"
            ? "Road: click scene to enter, then WASD to move, mouse to look. Esc to exit."
            : "Sky: left-click forward, right-click back, drag to look."}
        </Typography>
        <ToggleButtonGroup
          value={sceneView}
          exclusive
          onChange={(_, v) => v != null && setSceneView(v)}
          size="small"
          aria-label="scene view"
        >
          <ToggleButton value="road" aria-label="road view">Road</ToggleButton>
          <ToggleButton value="sky" aria-label="sky view">Sky</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {communalLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: (t) => t.spacing(1), mb: (t) => t.spacing(1) }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading communal quotes…</Typography>
        </Box>
      )}
      {communalError && (
        <Alert severity="warning" sx={{ mb: (t) => t.spacing(1) }}>
          We couldn’t load the communal quote list yet. You can still explore the scene.
        </Alert>
      )}
      {!communalLoading && !communalError && quoteCount === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: (t) => t.spacing(1) }}>
          No quotes in the scene yet. Add some from Personalize, or try again once the public list has content.
        </Typography>
      )}

      <Box className="three-scene" component="div">
        {sceneView === "road" ? (
          <RoadScene
            quoteList={quoteListForScene}
            onQuoteCount={setQuoteCount}
          />
        ) : (
          <Suspense fallback={<Typography variant="body2" color="text.secondary">Loading Sky view…</Typography>}>
            <SkyScene
              quoteList={quoteListForScene}
              onQuoteCount={setQuoteCount}
            />
          </Suspense>
        )}
      </Box>
    </>
  );
}

export default Commonplace;
